// ============================================
// One-Click Deploy - Flexible Model Selection
// ============================================

// Deployment Framework types
export type DeploymentFramework = 'vllm' | 'triton';

// Model precision types
export type ModelPrecision = 'FP16' | 'FP8' | 'FP4';

export interface DeploymentFrameworkDef {
  id: DeploymentFramework;
  name: string;
  description: string;
  icon: 'Zap' | 'Server';
}

export const DEPLOYMENT_FRAMEWORKS: DeploymentFrameworkDef[] = [
  {
    id: 'vllm',
    name: 'vLLM',
    description: 'High-throughput LLM serving with PagedAttention',
    icon: 'Zap',
  },
  {
    id: 'triton',
    name: 'Triton Inference Server',
    description: 'Multi-framework inference for embeddings, vision & more',
    icon: 'Server',
  },
];

// Model ability types
export type ModelAbility = 'text' | 'vision' | 'reasoning' | 'tools' | 'grammar' | 'embedding' | 'typhoon-ocr';

// Available model for selection
export interface DeployableModel {
  id: string;
  name: string;
  modelId: string; // HuggingFace model ID format: {org}/{model-name}
  description: string;
  category: 'llm' | 'vlm' | 'embedding' | 'guardrail' | 'ocr' | 'vision' | 'speech' | 'custom';
  vramRequired: number; // in GB
  parametersB: number; // in Billions (e.g., 1 = 1B, 70 = 70B)
  precision: ModelPrecision; // FP16, FP8, FP4
  framework: DeploymentFramework;
  isPreset: boolean;
  ability?: ModelAbility[]; // Optional: model capabilities
  // vLLM overrides for custom models
  toolParser?: string; // e.g., 'hermes', 'llama3_json', 'mistral'
  reasoningTemplate?: string; // e.g., 'qwen3', 'deepseek_r1'
}

// Preset models catalog
export const DEPLOYABLE_MODELS: DeployableModel[] = [
  // ============================================
  // vLLM Models (LLM, OCR, Guardrail categories)
  // ============================================
  {
    id: 'GPT-OSS-120B',
    name: 'GPT-OSS-120B',
    modelId: 'openai/gpt-oss-120b',
    description: 'OpenAI Open Source large language model',
    category: 'llm',
    vramRequired: 70,
    parametersB: 120,
    precision: 'FP4',
    framework: 'vllm',
    isPreset: true,
    ability: ["text","reasoning","tools","grammar"]
  },
  {
    id: 'GPT-OSS-20B',
    name: 'GPT-OSS-20B',
    modelId: 'openai/gpt-oss-20b',
    description: 'OpenAI Open Source large language model',
    category: 'llm',
    vramRequired: 20,
    parametersB: 20,
    precision: 'FP4',
    framework: 'vllm',
    isPreset: true,
    ability: ["text","reasoning","tools","grammar"]
  },
  {
    id: 'Qwen3-VL-235B-A22B-Instruct',
    name: 'Qwen3-VL-235B-A22B-Instruct',
    modelId: 'Qwen/Qwen3-VL-235B-A22B-Instruct-FP8',
    description: 'Alibaba Open Source large language model',
    category: 'vlm',
    vramRequired: 240,
    parametersB: 235,
    precision: 'FP8',
    framework: 'vllm',
    isPreset: true,
    ability: ["text","vision","tools","grammar"]
  },
  {
    id: 'Qwen3-VL-30B-A3B-Instruct',
    name: 'Qwen3-VL-30B-A3B-Instruct',
    modelId: 'Qwen/Qwen3-VL-30B-A3B-Instruct-FP8',
    description: 'Alibaba Open Source large language model',
    category: 'vlm',
    vramRequired: 30,
    parametersB: 30,
    precision: 'FP8',
    framework: 'vllm',
    isPreset: true,
    ability: ["text","vision","tools","grammar"]
  },
  {
    id: 'Qwen3-VL-32B-Instruct',
    name: 'Qwen3-VL-32B-Instruct',
    modelId: 'Qwen/Qwen3-VL-32B-Instruct-FP8',
    description: 'Alibaba Open Source large language model',
    category: 'vlm',
    vramRequired: 32,
    parametersB: 32,
    precision: 'FP8',
    framework: 'vllm',
    isPreset: true,
    ability: ["text","vision","tools","grammar"]
  },
  {
    id: 'Qwen3-VL-8B-Instruct',
    name: 'Qwen3-VL-8B-Instruct',
    modelId: 'Qwen/Qwen3-VL-8B-Instruct-FP8',
    description: 'Alibaba Open Source large language model',
    category: 'vlm',
    vramRequired: 8,
    parametersB: 8,
    precision: 'FP8',
    framework: 'vllm',
    isPreset: true,
    ability: ["text","vision","tools","grammar"]
  },
  {
    id: 'llama3.3-70b',
    name: 'Llama 3.3 70B Instruct',
    modelId: 'nvidia/Llama-3.3-70B-Instruct-NVFP4',
    description: 'Meta Llama 3.3 large language model',
    category: 'llm',
    vramRequired: 70,
    parametersB: 70,
    precision: 'FP4',
    framework: 'vllm',
    isPreset: true,
    ability: ["text","tools","grammar"]
  },
  {
    id: 'Typhoon-ocr1.5-2b',
    name: 'Typhoon-ocr1.5-2b',
    modelId: 'typhoon-ai/typhoon-ocr1.5-2b',
    description: 'SCB10X large language model',
    category: 'vlm',
    vramRequired: 6,
    parametersB: 2,
    precision: 'FP16',
    framework: 'vllm',
    isPreset: true,
    ability: ["text","vision","typhoon-ocr"]
  },
  {
    id: 'Typhoon2.5-qwen3-30b-a3b',
    name: 'Typhoon2.5-qwen3-30b-a3b',
    modelId: 'typhoon-ai/typhoon2.5-qwen3-30b-a3b',
    description: 'SCB10X large language model',
    category: 'llm',
    vramRequired: 60,
    parametersB: 30,
    precision: 'FP16',
    framework: 'vllm',
    isPreset: true,
    ability: ["text","tools","grammar"]
  },

  // vllm 0.14.1 not support. waiting for next release.
  { 
    id: 'GLM-4.7-Flash',
    name: 'GLM 4.7 Flash',
    modelId: 'GadflyII/GLM-4.7-Flash-NVFP4',
    description: 'ZAI large language model',
    category: 'llm',
    vramRequired: 30,
    parametersB: 30,
    precision: 'FP4',
    framework: 'vllm',
    isPreset: true,
    ability: ["text","tools","grammar"]
  },

  // ============================================
  // Triton Models (Embedding, Vision, Speech, etc.)
  // ============================================
  {
    id: 'bert-base-embedding',
    name: 'BERT-Base Embedding',
    modelId: 'google-bert/bert-base-uncased',
    description: 'General-purpose text embeddings',
    category: 'embedding',
    vramRequired: 4,
    parametersB: 0.1,
    precision: 'FP16',
    framework: 'triton',
    isPreset: true,
  },
  {
    id: 'sentence-transformers',
    name: 'Sentence Transformers',
    modelId: 'sentence-transformers/all-MiniLM-L6-v2',
    description: 'Semantic search embeddings',
    category: 'embedding',
    vramRequired: 4,
    parametersB: 0.4,
    precision: 'FP16',
    framework: 'triton',
    isPreset: true,
  },
  {
    id: 'bge-large-embedding',
    name: 'BGE-Large Embedding',
    modelId: 'BAAI/bge-large-en-v1.5',
    description: 'High-quality text embeddings for RAG',
    category: 'embedding',
    vramRequired: 8,
    parametersB: 0.3,
    precision: 'FP16',
    framework: 'triton',
    isPreset: true,
  },
  {
    id: 'yolov8-detection',
    name: 'YOLOv8 Detection',
    modelId: 'ultralytics/yolov8',
    description: 'Real-time object detection',
    category: 'vision',
    vramRequired: 8,
    parametersB: 0.05,
    precision: 'FP16',
    framework: 'triton',
    isPreset: true,
  },
  {
    id: 'resnet50-classifier',
    name: 'ResNet-50 Classifier',
    modelId: 'microsoft/resnet-50',
    description: 'Image classification model',
    category: 'vision',
    vramRequired: 2,
    parametersB: 0.03,
    precision: 'FP16',
    framework: 'triton',
    isPreset: true,
  },
  {
    id: 'whisper-medium',
    name: 'Whisper Medium',
    modelId: 'openai/whisper-medium',
    description: 'Speech-to-text transcription',
    category: 'speech',
    vramRequired: 5,
    parametersB: 0.8,
    precision: 'FP16',
    framework: 'triton',
    isPreset: true,
  },
  {
    id: 'whisper-large',
    name: 'Whisper Large',
    modelId: 'openai/whisper-large-v3',
    description: 'High-accuracy speech recognition',
    category: 'speech',
    vramRequired: 10,
    parametersB: 1.5,
    precision: 'FP16',
    framework: 'triton',
    isPreset: true,
  },
  {
    id: 'guardrail-classifier',
    name: 'Guardrail Classifier',
    modelId: 'protectai/deberta-v3-base-prompt-injection-v2',
    description: 'Content moderation classifier',
    category: 'guardrail',
    vramRequired: 4,
    parametersB: 0.1,
    precision: 'FP16',
    framework: 'triton',
    isPreset: true,
  },
  {
    id: 'ocr-paddleocr',
    name: 'PaddleOCR',
    modelId: 'PaddlePaddle/PaddleOCR',
    description: 'Document text extraction',
    category: 'ocr',
    vramRequired: 4,
    parametersB: 0.05,
    precision: 'FP16',
    framework: 'triton',
    isPreset: true,
  },
];

// MIG Profile definitions with capacity
export interface MigProfileDef {
  id: string;
  name: string;
  vramCapacity: number; // GB
  slices: number;
}

export const MIG_PROFILE_DEFS: MigProfileDef[] = [
  { id: '1g.18gb', name: '1g.18gb', vramCapacity: 18, slices: 1 },
  { id: '1g.35gb', name: '1g.35gb', vramCapacity: 35, slices: 1 },
  { id: '2g.35gb', name: '2g.35gb', vramCapacity: 35, slices: 2 },
  { id: '3g.71gb', name: '3g.71gb', vramCapacity: 71, slices: 3 },
  { id: '4g.71gb', name: '4g.71gb', vramCapacity: 71, slices: 4 },
  { id: '7g.141gb', name: '7g.141gb', vramCapacity: 141, slices: 7 },
];

// Find eligible MIG profiles for a given VRAM requirement
export const getEligibleMigProfiles = (vramRequired: number): MigProfileDef[] => {
  return MIG_PROFILE_DEFS.filter((profile) => profile.vramCapacity >= vramRequired);
};

// Auto-assign the smallest MIG profile that fits the VRAM requirement
export const autoAssignMigProfile = (vramRequired: number): MigProfileDef | null => {
  const eligible = getEligibleMigProfiles(vramRequired);
  if (eligible.length === 0) return null;
  // Return smallest profile that fits (sorted by slices)
  return eligible.sort((a, b) => a.slices - b.slices)[0];
};

// Selected model with assigned MIG profile
export interface SelectedModel {
  model: DeployableModel;
  migProfile: MigProfileDef;
}

// Calculate total slices used by selected models
export const calculateTotalSlices = (selectedModels: SelectedModel[]): number => {
  return selectedModels.reduce((total, sm) => total + sm.migProfile.slices, 0);
};

// Calculate total parameters (in billions) of selected models
export const calculateTotalParametersB = (selectedModels: SelectedModel[]): number => {
  return selectedModels.reduce((total, sm) => total + sm.model.parametersB, 0);
};

// Calculate recommended volume size (2x total billion parameters, minimum 100GB for One-Click Deployment)
export const calculateRecommendedVolumeSize = (selectedModels: SelectedModel[]): number => {
  const totalParams = calculateTotalParametersB(selectedModels);
  const recommendedSize = Math.ceil(totalParams * 2);
  // Minimum 100GB for One-Click Deployment, round up to nearest 10
  return Math.max(100, Math.ceil(recommendedSize / 10) * 10);
};

// Check if models can fit within H200 (7 slices)
export const canFitModels = (selectedModels: SelectedModel[]): boolean => {
  return calculateTotalSlices(selectedModels) <= 7;
};

// Stack Deployment Info (result card)
export interface StackDeploymentInfo {
  stackName: string;
  framework: DeploymentFramework;
  frameworkName: string;
  models: {
    id: string;
    name: string;
    endpoint: string;
    migProfile: string;
    status: 'ready' | 'deploying' | 'error';
  }[];
  apiKey: string;
  deployedAt: string;
}

export const mockStackDeploymentInfo = (
  taskId: string,
  selectedModels: SelectedModel[],
  framework: DeploymentFramework = 'vllm'
): StackDeploymentInfo => {
  const frameworkDef = DEPLOYMENT_FRAMEWORKS.find((f) => f.id === framework);
  return {
    stackName: 'Custom Stack',
    framework: framework,
    frameworkName: frameworkDef?.name || framework,
    models: selectedModels.map((sm) => ({
      id: sm.model.id,
      name: sm.model.name,
      endpoint: `https://api.float16.cloud/v1/gpu/${taskId}/${sm.model.id}`,
      migProfile: sm.migProfile.id,
      status: 'ready' as const,
    })),
    apiKey: `sk-stack-${taskId.slice(0, 8)}-${Math.random().toString(36).slice(2, 10)}`,
    deployedAt: new Date().toISOString(),
  };
};
